import { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, FolderOpen, Globe, Coins } from 'lucide-react';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Word-by-word stagger animation for the greeting text.
 */
function StaggerText({ text, className, style, delay = 0 }) {
  const prefersReducedMotion = useReducedMotion();
  const words = text.split(' ');

  if (prefersReducedMotion) {
    return <span className={className} style={style}>{text}</span>;
  }

  return (
    <span className={className} style={style}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: delay + i * 0.06, duration: 0.4, ease: 'easeOut' }}
          style={{ display: 'inline-block', marginRight: '0.3em' }}
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
}

/**
 * Stat pill badge with icon.
 */
function StatPill({ icon: Icon, label, color, delay = 0 }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.span
      initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.3 }}
      className="flex items-center gap-1.5"
      style={{
        fontSize: 11, fontFamily: 'var(--orbis-mono)', fontWeight: 500,
        color: color || 'var(--orbis-t3)',
        background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
        borderRadius: 100, padding: '4px 10px',
      }}
    >
      <Icon className="w-3 h-3" style={{ opacity: 0.7 }} />
      {label}
    </motion.span>
  );
}

/**
 * Hero Welcome Bar — time-aware greeting + pill badges + resume button.
 */
export default function HeroWelcome({ user, projects, credits, onNavigateToProject }) {
  const prefersReducedMotion = useReducedMotion();

  const { greeting, pills, resumeProject, resumeLabel } = useMemo(() => {
    const greeting = `${getGreeting()}, ${user?.name?.split(' ')[0] || 'there'}.`;

    const inProgress = projects.filter(p => p.magic_mode && p.magic_state?.current_stage && p.magic_state.current_stage !== 'deploy').length;
    const deployed = projects.filter(p => p.status === 'deployed' || p.custom_domain).length;

    const pills = [];
    if (projects.length > 0) pills.push({ icon: FolderOpen, label: `${projects.length} project${projects.length !== 1 ? 's' : ''}`, color: 'var(--orbis-t2)' });
    if (deployed > 0) pills.push({ icon: Globe, label: `${deployed} live`, color: 'var(--orbis-positive)' });
    if (credits?.lifetimeUsed > 0) pills.push({ icon: Coins, label: `${credits.lifetimeUsed} credits used`, color: 'var(--orbis-t3)' });

    const sortedProjects = [...projects].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    const resumeProject = sortedProjects.find(p =>
      p.magic_mode && p.magic_state?.current_stage && p.magic_state.current_stage !== 'deploy'
    ) || sortedProjects[0] || null;
    const resumeLabel = resumeProject ? `Resume ${resumeProject.name}` : null;

    return { greeting, pills, resumeProject, resumeLabel };
  }, [user, projects, credits]);

  return (
    <div style={{ padding: '8px 0 20px', position: 'relative' }}>
      {/* Greeting */}
      <div style={{ marginBottom: 10 }}>
        <StaggerText
          text={greeting}
          delay={0.1}
          style={{
            fontSize: 28,
            fontWeight: 200,
            fontFamily: 'var(--orbis-font-display)',
            letterSpacing: '-0.02em',
            color: 'var(--orbis-warm)',
          }}
        />
      </div>

      {/* Pill badges + Resume button */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {pills.map((pill, i) => (
            <StatPill key={i} icon={pill.icon} label={pill.label} color={pill.color} delay={0.4 + i * 0.08} />
          ))}
        </div>

        {resumeProject && (
          <motion.button
            initial={prefersReducedMotion ? {} : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7, type: 'spring', stiffness: 300, damping: 25 }}
            onClick={() => onNavigateToProject(resumeProject)}
            className="flex items-center gap-1.5 cursor-pointer hover:gap-2.5 transition-all shrink-0"
            style={{
              background: 'none', border: '0.5px solid rgba(184,204,223,0.25)',
              padding: '5px 14px', borderRadius: 100,
              fontSize: 12, fontWeight: 500, fontFamily: 'var(--orbis-font)',
              color: 'var(--orbis-blue)',
            }}
          >
            {resumeLabel}
            <ArrowRight className="w-3 h-3" />
          </motion.button>
        )}
      </div>
    </div>
  );
}
